import { ReactNode, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { TableWithCustomRender } from '../TableWithCustomRender';
import { useAppSelector } from '../../../common/hooks';
import {
    DEFAULT_CURRENCY_LOCALE,
    DEFAULT_CURRENCY_SYMBOL,
    REALLOC_FREQUENCY_COLUMN_COUNT
} from '../../../common/constants';

export type ContributionScheduleAccount = {
    name: string;
    subLabel?: string;
    dateValueList: { date: string; value: number }[];
};

type Props = {
    accounts: ContributionScheduleAccount[];
    targetDate?: string;
    firstColumnLabel?: string;
    firstColumnWidth?: string;
    showNowColumn?: boolean;
    showTotalRow?: boolean;
    totalRowValues?: { date: string; value: number }[];
    trailingEmptyStateLabel?: string;
    horizontalScroll?: boolean;
    stickyFirstColumn?: boolean;
    maxHeight?: string;
    bodyRowHeight?: string;
    // eslint-disable-next-line no-unused-vars
    renderNameCell?: (row: any) => ReactNode;
};

const TOTAL_ROW_ID = '__total__';

const formatScheduleValue = (value?: number) =>
    value == null
        ? '-'
        : new Intl.NumberFormat(DEFAULT_CURRENCY_LOCALE, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          }).format(value);

const monthIndex = (date: string) => {
    const d = dayjs(date, 'DD-MM-YYYY');
    return d.year() * 12 + d.month();
};
// OBS: 7
// Keeps a wide group-header label horizontally centered within the scrollable viewport instead of drifting with its wide cell during horizontal scroll.
const CenteredScrollLabel = ({
    enabled,
    leftInset,
    children
}: {
    enabled: boolean;
    leftInset: number;
    children: ReactNode;
}) => {
    const ref = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!enabled || !el) return;
        const container = el.closest('.MuiTableContainer-root') as HTMLElement | null;
        if (!container) return;
        const update = () => {
            el.style.width = `${Math.max(0, container.clientWidth - leftInset)}px`;
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(container);
        return () => ro.disconnect();
    }, [enabled, leftInset, children]);

    if (!enabled) return <>{children}</>;

    // Pinned to the right of the sticky first column; fixed width spans the visible area and the
    // centered flex keeps the text mid-viewport as the wide cell scrolls beneath it.
    return (
        <Box
            component="span"
            ref={ref}
            sx={{
                position: 'sticky',
                left: `${leftInset}px`,
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                maxWidth: '100%'
            }}
        >
            {children}
        </Box>
    );
};

export const ContributionScheduleTable = ({
    accounts = [],
    targetDate,
    firstColumnLabel,
    firstColumnWidth = '200px',
    showNowColumn = false,
    showTotalRow = false,
    totalRowValues,
    trailingEmptyStateLabel,
    horizontalScroll = false,
    stickyFirstColumn = false,
    maxHeight,
    bodyRowHeight = '80px',
    renderNameCell
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const currency = useAppSelector((state) => state.global.globalConfig?.currencySymbol) || DEFAULT_CURRENCY_SYMBOL;
    const scheduleFrequency = useAppSelector((state) => state.global.goeConfig?.scheduleFrequency);
    // Union of dates across all accounts so a value returned only by a later account is not dropped.
    const rawDates = Array.from(
        new Set(accounts.flatMap((account) => account.dateValueList?.map((item) => item.date) ?? []))
    ).sort((a, b) => monthIndex(a) - monthIndex(b));

    const firstColLabel = firstColumnLabel ?? t('FUNDING_SOURCE_HEADER');

    const frequencyColumnCount = scheduleFrequency ? REALLOC_FREQUENCY_COLUMN_COUNT[scheduleFrequency] : undefined;
    const baseDates =
        frequencyColumnCount && rawDates.length > 0 && rawDates.length < frequencyColumnCount
            ? [
                  ...rawDates,
                  ...Array.from({ length: frequencyColumnCount - rawDates.length }, (_, i) =>
                      dayjs(rawDates[rawDates.length - 1], 'DD-MM-YYYY')
                          .add(i + 1, 'month')
                          .format('DD-MM-YYYY')
                  )
              ]
            : rawDates;

    // The goal matures on its target month; that month shows "Goal Matured" and later months are dropped.
    const maturedIndex = targetDate ? baseDates.findIndex((date) => monthIndex(date) >= monthIndex(targetDate)) : -1;
    const maturedDate = maturedIndex >= 0 ? baseDates[maturedIndex] : null;
    const dates = maturedIndex >= 0 ? baseDates.slice(0, maturedIndex + 1) : baseDates;

    // Columns beyond the last returned month are synthetic padding; collapse them into one panel.
    const returnedDatesCount = Math.min(rawDates.length, dates.length);
    const trailingMergedFromKey =
        trailingEmptyStateLabel && dates.length > returnedDatesCount ? dates[returnedDatesCount] : null;

    // "Now" column pulls the first schedule month out as a standalone column outside the group header.
    const nowDate = showNowColumn ? dates[0] : null;
    const nowMonthLabel = nowDate ? `(${dayjs(nowDate, 'DD-MM-YYYY').format('MMM YYYY')})` : '';
    const nowHeader = nowDate ? (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                fontWeight: 500,
                lineHeight: '18px'
            }}
        >
            <span>{t('SCHEDULE_NOW')}</span>
            <span>{nowMonthLabel}</span>
        </Box>
    ) : null;
    const groupDates = nowDate ? dates.slice(1) : dates;

    const renderNameColumn = (value: any, row: any) => {
        if (row?.id === TOTAL_ROW_ID) {
            return <span style={{ fontWeight: 600 }}>{t('TOTAL')}</span>;
        }
        return renderNameCell ? renderNameCell(row) : value;
    };

    const renderValueCell = (value: number, row: any) =>
        row?.id === TOTAL_ROW_ID ? (
            value == null ? (
                ''
            ) : (
                <span style={{ fontWeight: 600 }}>{formatScheduleValue(value)}</span>
            )
        ) : (
            formatScheduleValue(value)
        );

    const headers = [
        {
            label: firstColLabel,
            key: 'name',
            align: 'left' as const,
            width: firstColumnWidth,
            cellSx: {
                wordBreak: 'break-word' as const,
                whiteSpace: 'normal' as const,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px'
            },
            render: renderNameColumn
        },
        ...(nowDate
            ? [
                  {
                      label: `${t('SCHEDULE_NOW')} ${nowMonthLabel}`,
                      key: nowDate,
                      align: 'right' as const,
                      width: '110px',
                      headerSx: { fontWeight: '500 !important', whiteSpace: 'nowrap' },
                      render: renderValueCell
                  }
              ]
            : []),
        ...groupDates.map((date, dateIndex) => ({
            label: dayjs(date, 'DD-MM-YYYY').format('MMM YYYY'),
            key: date,
            align: 'right' as const,
            width: '130px',
            headerSx: {
                fontWeight: '700 !important',
                color: `${theme.palette.semantic.text.muted} !important`,
                whiteSpace: 'nowrap',
                ...(dateIndex === 0
                    ? { '&&&&': { borderLeft: `1px solid ${theme.palette.neutral[200]}` } }
                    : {})
            },
            cellSx: {
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '19px',
                whiteSpace: 'nowrap'
            },
            render: renderValueCell
        }))
    ];

    const groupHeaders = [
        { label: firstColLabel, colSpan: 1, align: 'left' as const, rowSpan: 2, width: firstColumnWidth },
        ...(nowDate
            ? [
                  {
                      label: nowHeader,
                      colSpan: 1,
                      align: 'right' as const,
                      rowSpan: 2,
                      width: '110px',
                      sx: { fontWeight: '500 !important' }
                  }
              ]
            : []),
        {
            label: (
                /** OBS: 7 */
                <CenteredScrollLabel
                    enabled={horizontalScroll}
                    leftInset={stickyFirstColumn ? parseInt(firstColumnWidth, 10) || 0 : 0}
                >
                    {t('CONTRIBUTIONS_SCHEDULE_HEADER', { currency })}
                </CenteredScrollLabel>
            ),
            colSpan: groupDates.length,
            // OBS: 7
            // Left-align so the sticky label's static position starts at the cell's left edge;
            // the label itself handles visual centering within the viewport.
            align: (horizontalScroll ? 'left' : 'center') as 'left' | 'center',
            width: '1100px',
            sx: { '&&&&': { borderBottom: `1px solid ${theme.palette.neutral[200]}` } }
        }
    ];

    const dataSource = accounts.map((account, index) => {
        const row: Record<string, any> = {
            id: `${account.name}-${index}`,
            name: account.name,
            subLabel: account.subLabel
        };
        account.dateValueList?.forEach((item) => {
            row[item.date] = item.value;
        });
        return row;
    });

    if (showTotalRow) {
        const totalRow: Record<string, any> = { id: TOTAL_ROW_ID, name: t('TOTAL') };
        totalRowValues?.forEach((item) => {
            totalRow[item.date] = item.value;
        });
        dataSource.push(totalRow);
    }

    const mergedCellConfig = maturedDate
        ? {
              fromColumnKey: maturedDate,
              content: t('GOAL_MATURED'),
              sx: {
                  backgroundColor: theme.palette.semantic.bg.subtle,
                  color: theme.palette.semantic.text.default,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '19px',
                  whiteSpace: 'nowrap' as const
              }
          }
        : trailingMergedFromKey
          ? {
                fromColumnKey: trailingMergedFromKey,
                content: trailingEmptyStateLabel,
                sx: {
                    backgroundColor: theme.palette.semantic.bg.subtle,
                    color: theme.palette.semantic.text.muted,
                    whiteSpace: 'normal' as const,
                    wordBreak: 'break-word' as const,
                    overflowWrap: 'anywhere' as const,
                    fontSize: '14px',
                    lineHeight: 1.4,
                    px: 1
                }
            }
          : undefined;

    return (
        <TableWithCustomRender
            headers={headers}
            dataSource={dataSource}
            groupHeaders={groupHeaders}
            variant="ftds"
            embedded="all"
            rowHeight="40px"
            bodyRowHeight={bodyRowHeight}
            horizontalScroll={horizontalScroll}
            stickyFirstColumn={stickyFirstColumn}
            maxHeight={maxHeight}
            trailingMergedCell={mergedCellConfig}
        />
    );
};
